import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Optional, Output, ViewChild } from '@angular/core';
import { ActivatedRoute, ParamMap } from '@angular/router';
import { Keyboard } from '@capacitor/keyboard';
import { FingerprintAIO } from '@ionic-native/fingerprint-aio/ngx';
import { IonInput, NavController } from '@ionic/angular';
import { EMPTY, Observable, Subscription, concat, defer, from, of, throwError } from 'rxjs';
import { catchError, filter, finalize, map, mergeMap, takeUntil, tap } from 'rxjs/operators';
import { ComponentBase } from '../../helpers/ComponentBase';
import { LifeCycleObserverComponentBase } from '../../helpers/LifeCycleObserverComponentBase';
import { ArrayHelper } from '../../helpers/arrayHelper';
import { StringHelper } from '../../helpers/stringHelper';
import { UserData } from '../../model/application/UserData';
import { EAuthenticationAction } from '../../model/authenticator/EAuthenticationAction';
import { IAuthenticatorParams } from '../../model/authenticator/IAuthenticatorParams';
import { ConfigData } from '../../model/config/ConfigData';
import { IContact } from '../../model/contacts/IContact';
import { ELifeCycleEvent } from '../../model/lifeCycle/ELifeCycleEvent';
import { ILifeCycleEvent } from '../../model/lifeCycle/ILifeCycleEvent';
import { Credentials } from '../../model/security/Credentials';
import { EUnlockMode } from '../../model/security/EUnlockMode';
import { ICredentialsDocument } from '../../model/security/ICredentialsDocument';
import { ISecuritySettingsDocument } from '../../model/security/ISecuritySettingsDocument';
import { IUpdate } from '../../model/update/IUpdate';
import { IWorkspace } from '../../model/workspaces/IWorkspace';
import { UserContactService } from '../../modules/contacts/userContact/services/user-contact.service';
import { EFlag } from '../../modules/flags/models/EFlag';
import { Loader } from '../../modules/loading/Loader';
import { ModalService } from '../../modules/modal/services/modal.service';
import { PerformanceManager } from '../../modules/performance/PerformanceManager';
import { EPermissionsFlag } from '../../modules/permissions/models/EPermissionsFlag';
import { PinService } from '../../modules/security/pin/services/pin.service';
import { SiteSelectorModalComponent } from '../../modules/sites/components/site-selector-modal/site-selector-modal.component';
import { ISite } from '../../modules/sites/models/isite';
import { SitesService } from '../../modules/sites/services/sites.service';
import { ApplicationService } from '../../services/application.service';
import { FlagService } from '../../services/flag.service';
import { GlobalDataService } from '../../services/global-data.service';
import { ShowMessageParamsPopup } from '../../services/interfaces/ShowMessageParamsPopup';
import { ShowMessageParamsToast } from '../../services/interfaces/ShowMessageParamsToast';
import { LoadingService } from '../../services/loading.service';
import { PageManagerService } from '../../services/pageManager.service';
import { PlatformService } from '../../services/platform.service';
import { SecurityService } from '../../services/security.service';
import { UiMessageService } from '../../services/uiMessage.service';
import { UpdateService } from '../../services/update.service';
import { WorkspaceService } from '../../services/workspace.service';
import { DynamicPageComponent } from '../dynamicPage/dynamicPage.component';

type UnlockMode = "biometric" | "face" | "finger";

@Component({
	selector: "calao-authenticator",
	templateUrl: 'authenticator.component.html',
	styleUrls: ['./authenticator.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class AuthenticatorComponent extends LifeCycleObserverComponentBase implements OnInit, OnDestroy, AfterViewInit {

	//#region FIELDS

	public static readonly C_COMPONENT_NAME = "authenticator";

	/** Clé de recherche du document NoSQL de paramétrage dans la vue par type. */
	private static readonly C_AUTHENTICATION_COMPONENT_TYPE_KEY: string = AuthenticatorComponent.C_COMPONENT_NAME;

	private static readonly C_LOG_ID = "AUTH.C::";

	private static readonly C_NO_AVAILABLE_UPDATE_VERSION = "Aucune";

	/** Evénement du clic sur le lien des changelogs. */
	@Output("onChangelogsClicked") private readonly moChangelogsClickedEvent = new EventEmitter<void>();

	private mbAuthenticatingUser = false;
	/** Fonction d'abonnement pour désactiver le backButton natif, l'exécuter pour se désabonner. */
	private readonly moBackButtonSubscription: Subscription;

	private readonly moAuthenticationPerformanceManager = new PerformanceManager;

	/** Url de redirection après authentification de l'utilisateur */
	private msRedirectUrl: string;
	private isDevMode: boolean = ConfigData.environment.id == "dev1";

	/** Si aucune mise à jour 'Aucune', sinon la version de la mise à jour disponible. */
	private msAvailableUpdateVersion: string;

	//#endregion

	//#region PROPERTIES

	@ViewChild("passwordInput") public passwordInput: IonInput;

	@ViewChild("pinInput") public pinInput: IonInput;

	/** Paramètre du composant. Si non injecté récupère ceux de `ConfigData.authentication.authenticatorParams`, si absent récupère dans la base de données de `AuthenticatorComponent.C_AUTHENTICATION_COMPONENT_TYPE_KEY`. */
	@Input() public params: IAuthenticatorParams;

	/** Paramètres du composant */
	public credentials = new Credentials();
	public unlockMode: UnlockMode;
	public expiredToken: boolean;
	/** Indique si la section d'authentification par login/mot de passe est écrasée. */
	public loginCollapsed = false;
	public versionNumber: string;
	public passwordInputType = "password";
	/** Retourne l'icône associée au type de biométrie de la plateforme. */
	public get biometricIconName(): string {
		// Android 9+ = "biometric" ; Android 8- = "finger" ; iOS = "faceid" ;
		return this.unlockMode === "biometric" || this.unlockMode === "finger" ? "finger-print" : "faceid";
	}

	/** Retourne les classes à appliquer sur l'icône de déverrouillage par biométrie sous forme de chaîne de caractères. */
	public get biometricIconClass(): string {
		return `ion-no-padding${this.unlockMode === "face" ? " face-id" : ""}`;
	}

	/** Retourne le type de déverrouillage par biométrie. */
	public get biometricType(): string {
		// Android 9+ = "biometric" ; Android 8- = "finger" ; iOS = "faceid" ;
		return this.unlockMode === "biometric" || this.unlockMode === "finger" ? "TouchID" : "FaceID";
	}

	/** Retourne l'icône associée à la visibilité du mot de passe. */
	public get passwordVisibilityIconName(): string {
		return this.passwordInputType === "password" ? "eye-off-outline" : "eye-outline";
	}

	public readonly isMobileApp: boolean;

	/**Retourne `true` si un PIN est sauvegardé, `false` sinon. */
	public get isSavedPin(): boolean {
		return this.isvcPin.isSaved;
	}

	public pinCollapsed = true;
	public pin: string;
	public readonly isStoreRelease: boolean = ConfigData.environment.storeRelease;

	public readonly changelogsUrl: string = ConfigData.appInfo.changelogsUrl;

	//#endregion

	//#region METHODS

	constructor(
		@Optional() poParentPage: DynamicPageComponent<ComponentBase>,
		/** Service de gestion des pages. */
		private isvcPageManager: PageManagerService,
		/** Service de gestion des empreintes digitales. */
		private ioFingerprintAIO: FingerprintAIO,
		/** Service de gestion des popups et toasts. */
		private isvcUiMessage: UiMessageService,
		/** Service de gestion de la sécurité. */
		private isvcSecurity: SecurityService,
		/** Service spinner */
		private isvcLoading: LoadingService,
		private isvcUserContact: UserContactService,
		private ioRoute: ActivatedRoute,
		private ioNavController: NavController,
		private isvcApplication: ApplicationService,
		private readonly isvcUpdate: UpdateService,
		private readonly isvcModal: ModalService,
		private readonly isvcWorkspace: WorkspaceService,
		private readonly isvcSites: SitesService,
		private readonly isvcPin: PinService,
		private readonly isvcFlag: FlagService,
		private readonly isvcGlobalData: GlobalDataService,
		psvcPlatform: PlatformService,
		poChangeDetector: ChangeDetectorRef
	) {
		super(poParentPage, poChangeDetector);

		if (!poParentPage)
			console.warn("AUTH.C::Pas de dynamic page passé en paramètre!");

		this.isMobileApp = psvcPlatform.isMobileApp;
		// On ne peut pas mettre de pipe avec la méthode `subscribeWithPriority`.
		this.moBackButtonSubscription = psvcPlatform.getBackButtonSubscription(() => { /* Ne fait rien. */ });
	}

	protected onLifeCycleEvent(poValue: ILifeCycleEvent): void {
		switch (poValue.data.value) {

			case ELifeCycleEvent.viewDidEnter:
				this.isvcLoading.dismiss(); // Suppression loader du lancement de l'app (app.component.core).
				break;
		}
	}

	public ngOnInit(): void {
		this.initComponent();

		this.ioRoute.queryParamMap.pipe(
			tap((poQueryParamMap: ParamMap) => this.msRedirectUrl = this.getDecodedRedirectUrl(poQueryParamMap.get("redirectUrl"))),
			takeUntil(this.destroyed$)
		).subscribe();

		switch (this.params.authenticationAction) {

			case EAuthenticationAction.route:
				if (this.params.loginEditEnabled !== false)
					this.params.loginEditEnabled = true;

				if (this.params.newAccountEnabled !== false)
					this.params.newAccountEnabled = true;
				break;

			case EAuthenticationAction.internal:
				this.params.loginEditEnabled = false;
				this.params.newAccountEnabled = false;
				break;
		}

		this.isvcSecurity.checkTokenExpiration()
			.pipe(
				tap((pbExpired: boolean) => {
					this.expiredToken = pbExpired;
					this.detectChanges();
				}),
				mergeMap(_ => concat(this.initCredentials(), this.initFingerPrintAuth(), this.tryUnlock())),
				takeUntil(this.destroyed$)
			)
			.subscribe();
	}

	public ngOnDestroy(): void {
		super.ngOnDestroy();

		this.moBackButtonSubscription.unsubscribe();
	}

	public ngAfterViewInit(): void {
		super.ngAfterViewInit();

		this.moAuthenticationPerformanceManager.markStart();
		// Permet de focus l'input du PIN
		if (this.pinInput)
			setTimeout(() => this.pinInput.setFocus(), 500);
	}

	/** Initialise l'attribut `this.params` si absent et initialise l'interface graphique. */
	private initComponent(): void {
		this.initParams();

		if (!StringHelper.isBlank(this.params.login) && !StringHelper.isBlank(this.params.password))
			this.credentials = Object.assign(new Credentials(), { login: this.params.login, password: this.params.password });

		if (ConfigData && ConfigData.appInfo)
			this.versionNumber = ConfigData.appInfo.appVersion;
	}

	/** Initialise l'attribut `this.params`. */
	private initParams(): void {
		if (this.params)
			return;

		if (ConfigData.authentication.authenticatorParams)
			this.params = ConfigData.authentication.authenticatorParams;
		else {
			this.isvcSecurity.getAuthenticatorConfig(AuthenticatorComponent.C_AUTHENTICATION_COMPONENT_TYPE_KEY)
				.pipe(
					filter((poResult: IAuthenticatorParams) => !!poResult),
					tap((poResult: IAuthenticatorParams) => this.params === poResult),
					takeUntil(this.destroyed$))
				.subscribe(_ => { }, poError => console.error("AUTH.C:: Erreur récupération config composant :", poError));
		}
	}

	private tryUnlock(): Observable<boolean> {
		return this.isvcSecurity.getSavedSecuritySettings()
			.pipe(
				mergeMap((poSecuritySettings: ISecuritySettingsDocument) => {
					if (this.credentials?.isValidToken()) {
						if (poSecuritySettings?.unlockMode === EUnlockMode.fingerprint)
							return this.unlockFingerprint();
						else if (poSecuritySettings?.unlockMode === EUnlockMode.pin)
							this.pinCollapsed = !(this.loginCollapsed = true);
					}
					return EMPTY;
				}),
				takeUntil(this.destroyed$)
			);
	}

	private initCredentials(): Observable<ICredentialsDocument> {
		return this.isvcSecurity.getUserCredentials()
			.pipe(
				filter((poResult: Credentials) => !!poResult),
				tap((poResultCredentials: Credentials) => {
					if (StringHelper.isBlank(poResultCredentials.login) && !StringHelper.isBlank(this.params.login))
						poResultCredentials.login = this.params.login;

					if (StringHelper.isBlank(poResultCredentials.password) && !StringHelper.isBlank(this.params.password))
						poResultCredentials.password = this.params.password;

					this.credentials = ConfigData.environment.excludeLogins?.includes(poResultCredentials.login) ? new Credentials() : poResultCredentials;
					this.detectChanges();
				}),
				catchError(poError => {
					console.error("AUTH.C:: Erreur récupération 'credentials' utilisateur :", poError);
					return throwError(poError);
				}),
				takeUntil(this.destroyed$)
			);
	}

	private initFingerPrintAuth(): Observable<UnlockMode> {
		return defer(() => this.ioFingerprintAIO.isAvailable())
			.pipe(
				tap((psUnlockMode: UnlockMode) => {
					if (this.credentials.isValidToken())
						this.unlockMode = psUnlockMode;
				}),
				finalize(() => {
					this.loginCollapsed = !!this.unlockMode && !this.expiredToken;
					this.detectChanges();
				}),
				catchError(poError => {
					console.warn("AUTH.C:: Fingerprint availability check failed. Fingerprint will be considered as unavailable.", poError);
					return EMPTY;
				}),
				takeUntil(this.destroyed$)
			);
	}

	/** Lance l'authentification par empreinte. */
	public onFingerprintUnlockButtonClick(): void {
		this.unlockFingerprint().subscribe();
	}

	private unlockFingerprint(): Observable<boolean> {
		return this.isvcSecurity.unlockFingerprint()
			.pipe(
				tap(() => this.setUserLoginInteractionDuration()),
				mergeMap(_ => this.isvcApplication.waitForFlag(EPermissionsFlag.isLoaded, true)),
				mergeMap(_ => this.isvcUserContact.getContact$()),
				mergeMap(_ => this.redirectAfterAuth(true)),
				catchError(poError => {
					console.error("AUTH.C:: Erreur déverrouillage par empreinte :", poError);
					this.expiredToken = true;
					return throwError(poError);
				})
			);
	}

	/** On authentifie l'utilisateur avec ses login mdp et on lance le comportement voulu.
	 * @param psLogin
	 * @param psPassword
	 */
	private authenticateUser(psLogin: string, psPassword: string): Observable<boolean> {
		this.mbAuthenticatingUser = true;

		return this.isvcSecurity.authenticateUser(psLogin, psPassword)
			.pipe(
				tap(() => this.setUserLoginInteractionDuration()),
				mergeMap(_ => this.isvcApplication.waitForFlag(EPermissionsFlag.isLoaded, true)),
				mergeMap(_ => this.isvcUserContact.getContact$()),
				mergeMap((poUserContact: IContact) => {

					if(ConfigData.appInfo.versionAppErgo && ConfigData.appInfo.versionAppErgo === "2")
					{
						this.params.initUserContactAfterAuth = false;
						return of(true);
					}

					const lbIsAppStorageUserContact: boolean = this.isvcUserContact.isInitialized();
					let lbIsUserInitialized$: Observable<boolean>;
					if (this.params.initUserContactAfterAuth && !lbIsAppStorageUserContact) {
							this.isvcUserContact.redirectToContactEditAsync(); // Redirige vers la fiche moi si la configuration le permet et si l'utilisateur de la base locale n'existe pas.
							return of(false);
					}
					else {
						if (lbIsAppStorageUserContact)
							lbIsUserInitialized$ = of(true);
						else {
							lbIsUserInitialized$ = this.isvcUserContact.saveContact$(poUserContact);
						}
						return lbIsUserInitialized$.pipe(
							mergeMap(_ => this.params.destinationPathAfterAuth ? this.ioNavController.navigateRoot(this.params.destinationPathAfterAuth) : of(true))
						);
					}
				}),
				finalize(() => {
					this.mbAuthenticatingUser = false;
					this.isvcLoading.dismiss();
				})
			);
	}

	private setUserLoginInteractionDuration(): void {
		this.isvcGlobalData.setData(
			ApplicationService.C_USER_LOGIN_INTERACTION_DURATION_KEY,
			this.moAuthenticationPerformanceManager.markEnd().measure()
		);
	}

	private redirectAfterAuth(pbShouldRedirect: boolean): Observable<any> {
		const loInit$ = this.params.selectSite ? this.selectCurrentSite() : of(undefined);

		return loInit$
			.pipe(
				tap(() => {
					if (pbShouldRedirect) {
						const versionAppErgo = localStorage.getItem("versionAppErgo");
						if (!versionAppErgo || versionAppErgo === "2") {
							this.ioNavController.navigateRoot('/').then(() => {
								window.location.reload();
						});
						} else if (StringHelper.isBlank(this.msRedirectUrl)) {
							this.isvcPageManager.goHome();
						}
						else {
							this.ioNavController.navigateRoot(this.msRedirectUrl);
						}
					}
					this.isvcFlag.setFlagValue(EFlag.appAvailable, true);
				})
			);
	}

	/** Vérification des informations d'authentification de l'utilisateur et initialisation des bases de données si correctes. */
	public validateUser(): void {
		if (StringHelper.isBlank(this.credentials.login) || StringHelper.isBlank(this.credentials.password))
			this.isvcUiMessage.showMessage(new ShowMessageParamsPopup({ message: "Tous les champs d'authentification sont requis.", header: "Attention" }));

		else if (!this.mbAuthenticatingUser) {
			Keyboard.hide().catch(() => { }); // https://dev.azure.com/calaosoft/osapp-project/_workitems/edit/536
			let loLoader: Loader;

			from(this.isvcLoading.create("Vérification des informations de connexion ..."))
				.pipe(
					tap((poLoader: Loader) => loLoader = poLoader),
					mergeMap((poLoader: Loader) => poLoader.present()),
					mergeMap(_ => this.authenticateUser(this.credentials.login, this.credentials.password)),
					tap(() => loLoader?.dismiss()),
					mergeMap((pbShouldRedirect: boolean) => this.redirectAfterAuth(pbShouldRedirect)),
					catchError(poError => { console.error("AUTH.C:: Erreur authentification utilisateur :", poError); return throwError(poError); }),
					finalize(() => loLoader?.dismiss())
				)
				.subscribe();
		}
	}

	/** Permet d'afficher la liste des sites sue lesquels on peut se connecter. Ne s'affiche pas si un seul site.  */
	private selectCurrentSite(): Observable<ISite> {
		let lsDefaultSiteId: string;

		return this.isvcWorkspace.getWorkspaceDocument(ArrayHelper.getFirstElement(this.isvcWorkspace.getUserWorkspaceIds()))
			.pipe(
				tap((poWorkspace: IWorkspace) => lsDefaultSiteId = poWorkspace.defaultSiteId),
				mergeMap(() => this.isvcSites.getUserSiteIds()),
				map((paSitesIds: string[]) => lsDefaultSiteId ? ArrayHelper.unique([lsDefaultSiteId, ...paSitesIds]) : paSitesIds),
				mergeMap((paSitesIds: string[]) => {
					if (paSitesIds.length === 0)
						return of(undefined);
					if (paSitesIds.length === 1)
						return this.isvcSites.getSite(paSitesIds[0]);
					return this.isvcModal.open<ISite>({
						component: SiteSelectorModalComponent,
						componentProps: {
							hasCloseButton: false,
							title: "Sélectionnez le site sur lequel se connecter",
							sitesIds: paSitesIds
						},
						backdropDismiss: false
					});
				}),
				tap((poSite: ISite) => {
					UserData.currentSite = poSite;
					UserData.currentSite.isDefaultSite = poSite._id === lsDefaultSiteId;
				}),
			);
	}

	public toggleLoginSection(): void {
		this.loginCollapsed = !this.loginCollapsed;
	}

	/** Met à zéro et le focus sur l'input `Password`. */
	public setPasswordFocus(): void {
		this.passwordInput.setFocus();
	}

	public onPasswordFocused(): void {
		this.passwordInput.value = "";
	}

	public onPinClicked(): void {
		this.pinCollapsed = !this.pinCollapsed;
	}

	public unlockPin(): void {
		this.isvcSecurity.unlockPin(this.pin)
			.pipe(
				tap(() => this.setUserLoginInteractionDuration()),
				mergeMap(_ => this.isvcApplication.waitForFlag(EPermissionsFlag.isLoaded, true)),
				mergeMap(_ => this.isvcUserContact.getContact$()),
				mergeMap(_ => this.redirectAfterAuth(true)),
				catchError(poError => {
					console.error("AUTH.C:: Erreur déverrouillage par code pin :", poError);
					this.isvcUiMessage.showMessage(new ShowMessageParamsToast({ message: `Code incorrect, ${this.isvcPin.getRemainingAttempts()} tentatives restantes.` }));
					return throwError(poError);
				})
			).subscribe();
	}

	private getDecodedRedirectUrl(psRedirectUrl: string): string {
		if (!StringHelper.isBlank(psRedirectUrl)) {
			let lsDecodedUrl: string;

			while (true) {
				lsDecodedUrl = decodeURIComponent(psRedirectUrl);

				if (lsDecodedUrl === psRedirectUrl)
					break;

				psRedirectUrl = lsDecodedUrl;
			};

			return psRedirectUrl;
		}
		else
			return psRedirectUrl;
	}

	public togglePasswordVisibility(): void {
		this.passwordInputType = this.passwordInputType === "password" ? "" : "password";
	}

	public checkUpdate(): void {
		this.showUpdatePopup().pipe(
			catchError(poError => {
				console.error(`${AuthenticatorComponent.C_LOG_ID}Error while checking updates.`, poError);
				return EMPTY;
			}),
			takeUntil(this.destroyed$)
		).subscribe();
	}

	public debugComponent(): void {
		this.ioNavController.navigateRoot("/debugComponent");
	}

	private showUpdatePopup(): Observable<IUpdate> {
		return this.isvcUpdate.popupUpdate().pipe(
			tap(
				(poUpdate: IUpdate) => {
					if (!poUpdate) { // Si aucune nouvelle mise à jour.
						if (this.msAvailableUpdateVersion === AuthenticatorComponent.C_NO_AVAILABLE_UPDATE_VERSION) {
							// Affichage d'un message pour indiquer à l'utilisateur qu'une recherche de mise à jour a bien été faite.
							this.isvcUiMessage.showMessage(new ShowMessageParamsPopup({ message: "Aucune mise à jour disponible.", header: "Mise à jour" }));
						}
						else {
							this.msAvailableUpdateVersion = AuthenticatorComponent.C_NO_AVAILABLE_UPDATE_VERSION;
						}
					}
					else
						this.msAvailableUpdateVersion = poUpdate.version;
				},
				poError => this.isvcUiMessage.showMessage(new ShowMessageParamsPopup({ header: "Problème lors de la recherche de mise à jour.", message: poError.message }))
			)
		);
	}

	/** Envoie un événement pour notifier que le lien des changelogs a été cliqué. */
	public raiseChangelogsClickedAction(): void {
		this.moChangelogsClickedEvent.emit();
	}

	//#endregion

}
